// Projects home and the new-project form.
import { listProjects, createProject, deleteProject, draftUrl, startCheckout, type Project, type Studio } from '../data';
import { exportProject, openBackup, restoreProject, type OpenBackup, type Progress } from '../backup';
import { PLAN, tourUrl } from '@archviewvr/config';
import { html, icon, $, $$, shell, formatBytes, formatLimit, toast, confirmDialog } from '../ui';
import { openTutorial } from '../tutorial/player';

const chip = (state: Project['state']) =>
  html`<span class="chip ${state}"><span class="dot"></span>${{ trial: 'Trial', published: 'Published', paused: 'Paused', archived: 'Archived' }[state]}</span>`;

export async function projectsView(root: HTMLElement, studio: Studio): Promise<void> {
  const page = shell(root, { studioName: studio.name, active: 'projects' });
  page.innerHTML = '<div class="loading-page">Loading projects</div>';
  let projects: Project[] = [];
  try { projects = await listProjects(studio.id); }
  catch (e) { page.innerHTML = html`<div class="empty"><h2>Couldn’t load your projects</h2><p class="muted">${e instanceof Error ? e.message : ''}</p></div>`.html; return; }

  const published = projects.filter((p) => p.state === 'published').length;
  const cards = projects.map((p) => {
    const n = p.panoramas?.[0]?.count ?? 0;
    const used = Math.min(100, (p.storage_bytes / p.storage_limit) * 100);
    const cover = p.start_pano_id ? draftUrl(p.id, `thumbs/${p.start_pano_id}.jpg`) : null;
    const actions = p.state === 'published'
      ? html`<button class="btn small" data-share="${p.slug}">${icon('copy', 14)}Share link</button><a class="btn small" href="#/p/${p.id}/analytics">${icon('chart', 14)}Analytics</a>`
      : n
        ? html`<a class="btn small" href="/viewer/?draft=${p.id}" target="_blank" rel="noopener">${icon('eye', 14)}Preview</a><button class="btn small primary" data-publish="${p.id}">${p.state === 'paused' ? 'Publish again' : 'Publish'} · $${PLAN.priceAud}/mo</button>`
        : html`<a class="btn small primary" href="#/p/${p.id}/upload">Upload renders</a>`;
    return html`<article class="pcard" data-state="${p.state}" data-search="${`${p.name} ${p.client_name ?? ''}`.toLowerCase()}">
      <button class="pc-more" data-more="${p.id}" aria-label="More for ${p.name}" aria-haspopup="menu" aria-expanded="false">${icon('more', 18)}</button>
      <a class="thumb" href="#/p/${p.id}">${cover ? html`<img src="${cover}" alt="" loading="lazy" onerror="this.remove()">` : ''}${chip(p.state)}</a>
      <div class="body">
        <div class="row"><a class="name" href="#/p/${p.id}">${p.name}</a><span class="meta">${n ? `${n} render${n === 1 ? '' : 's'}` : 'No renders yet'}</span></div>
        <div style="display:grid;gap:6px">
          <div class="bar"><i class="${p.state === 'trial' ? 'warn' : ''}" style="width:${used.toFixed(1)}%"></i></div>
          <div class="meta">${p.state === 'trial' ? `Trial · ${n} of ${PLAN.trialPanoLimit} renders · preview only` : `${formatBytes(p.storage_bytes)} of ${formatLimit(p.storage_limit)}`}</div>
        </div>
        <div class="pcard-actions">${actions}</div>
      </div>
    </article>`;
  });

  if (!projects.length) {
    page.innerHTML = html`
      <section class="welcome-hero">
        <div class="wh-text">
          <h1>Let’s make your first tour</h1>
          <p>Never made one before? The guided tour shows what to set up in your 3D software, what to export and how to upload it. It takes a few minutes, and you can skip any part you already know.</p>
          <div class="btn-row"><button class="btn primary big" id="guide">${icon('guide', 18)}Take the guided tour</button><a class="btn big" href="#/new">Start a project</a></div>
        </div>
        <div class="wh-media"><img src="/tutorial/tour.jpg" alt=""></div>
      </section>`.html;
    $('#guide', page).onclick = () => { void openTutorial(); };
    return;
  }

  const trials = projects.filter((p) => p.state === 'trial').length;
  const counts: Record<string, number> = { all: projects.length, published, trial: trials, paused: projects.filter((p) => p.state === 'paused').length };
  page.innerHTML = html`
    <div class="page-head">
      <div><h1>Projects</h1><div class="sub">${projects.length} project${projects.length === 1 ? '' : 's'} · ${published} published · ${trials} trial</div></div>
      <div class="btn-row">
        <label class="search-pill">${icon('search', 15)}<input type="search" id="p-search" placeholder="Search projects" aria-label="Search projects"></label>
        <button class="btn" id="guide">${icon('guide', 16)}Guide</button><button class="btn" id="import-new">${icon('upload', 16)}Import</button><a class="btn primary" href="#/new">${icon('plus', 16)}New project</a>
      </div>
    </div>
    ${projects.length > 2 ? html`<nav class="chips" aria-label="Filter">${(['all', 'published', 'trial', 'paused'] as const).filter((k) => k === 'all' || counts[k]).map((k) => html`<button data-filter="${k}" aria-pressed="${k === 'all' ? 'true' : 'false'}">${{ all: 'All', published: 'Published', trial: 'Trial', paused: 'Paused' }[k]}</button>`)}</nav>` : ''}
    <section class="cards">
      ${cards}
      <a class="newcard" href="#/new"><span class="plus">${icon('plus', 22)}</span><b>New project</b><span>Starts as a free trial with up to ${PLAN.trialPanoLimit} renders</span></a>
    </section>
    <section class="box exporter">
      <div class="ex-l"><img src="/favicon.png" alt=""><div><strong>ArchviewVR Toolkit for 3ds Max</strong><span>Places your cameras, makes a light proxy, renders every 360 and exports a folder ready to drop in.</span></div></div>
      <div class="btn-row"><button class="link-btn" id="ex-guide">How to use it</button><a class="btn" href="/tools/archviewvr-toolkit.ms" download>${icon('download', 15)}Download toolkit</a></div>
    </section>`.html;
  $('#guide', page).onclick = () => { void openTutorial(); };
  $('#ex-guide', page).onclick = () => { void openTutorial({ startAt: 'export' }); };

  // filter and search
  let filter = 'all';
  const apply = () => {
    const q = $<HTMLInputElement>('#p-search', page).value.trim().toLowerCase();
    $$<HTMLElement>('.pcard', page).forEach((c) => { c.hidden = (filter !== 'all' && c.dataset.state !== filter) || (!!q && !c.dataset.search!.includes(q)); });
  };
  $<HTMLInputElement>('#p-search', page).oninput = apply;
  $$<HTMLButtonElement>('[data-filter]', page).forEach((b) => (b.onclick = () => {
    filter = b.dataset.filter!; $$('[data-filter]', page).forEach((x) => x.setAttribute('aria-pressed', String(x === b))); apply();
  }));

  // card buttons
  $$<HTMLButtonElement>('[data-share]', page).forEach((b) => (b.onclick = async () => {
    try { await navigator.clipboard.writeText(tourUrl(b.dataset.share!)); toast('Tour link copied'); }
    catch { toast(tourUrl(b.dataset.share!), 'info', 8000); }
  }));
  $$<HTMLButtonElement>('[data-publish]', page).forEach((b) => (b.onclick = async () => {
    b.disabled = true; const t = b.textContent; b.textContent = 'Opening checkout…';
    try { await startCheckout(b.dataset.publish!); }
    catch (e) { toast(e instanceof Error ? e.message : 'Couldn’t open checkout', 'error', 8000); b.disabled = false; b.textContent = t; }
  }));

  // the ⋯ menu on each card: export, import, delete
  const reload = () => projectsView(root, studio);
  $$<HTMLButtonElement>('[data-more]', page).forEach((b) => (b.onclick = (ev) => {
    ev.preventDefault(); ev.stopPropagation();
    const p = projects.find((x) => x.id === b.dataset.more)!;
    openMenu(b, [
      { label: 'Export backup', icon: 'download', run: () => runExport(p) },
      { label: 'Import backup', icon: 'upload', run: () => runImport(p, reload) },
      { label: 'Delete project', icon: 'trash', danger: true, run: () => runDelete(p, reload) },
    ]);
  }));
  $('#import-new', page).onclick = () => importAsNew(studio);
}

// ------------------------------------------------------------------ project menu

type MenuItem = { label: string; icon: 'download' | 'upload' | 'trash'; danger?: boolean; run: () => void };

function openMenu(anchor: HTMLElement, items: MenuItem[]): void {
  document.querySelector('.menu-pop')?.remove();
  const menu = document.createElement('div');
  menu.className = 'menu-pop';
  menu.setAttribute('role', 'menu');
  menu.innerHTML = items.map((it, i) => html`<button role="menuitem" class="${it.danger ? 'danger' : ''}" data-i="${i}">${icon(it.icon, 16)}${it.label}</button>`.html).join('');
  document.body.append(menu);
  const r = anchor.getBoundingClientRect();
  menu.style.top = `${r.bottom + window.scrollY + 6}px`;
  menu.style.left = `${Math.max(8, r.right + window.scrollX - menu.offsetWidth)}px`;
  anchor.setAttribute('aria-expanded', 'true');
  const close = () => { menu.remove(); anchor.setAttribute('aria-expanded', 'false'); document.removeEventListener('pointerdown', outside, true); document.removeEventListener('keydown', esc); };
  const outside = (e: Event) => { if (!menu.contains(e.target as Node) && e.target !== anchor && !anchor.contains(e.target as Node)) close(); };
  const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { close(); anchor.focus(); } };
  document.addEventListener('pointerdown', outside, true);
  document.addEventListener('keydown', esc);
  menu.querySelectorAll<HTMLButtonElement>('button').forEach((btn) => (btn.onclick = () => { close(); items[Number(btn.dataset.i)].run(); }));
  menu.querySelector<HTMLButtonElement>('button')?.focus();
}

/** A progress panel over the page while a backup is written or read. */
function progressPanel(title: string): { say: Progress; done: () => void } {
  const overlay = document.createElement('div');
  overlay.className = 'overlay';
  overlay.innerHTML = html`<div class="panel"><h2>${title}</h2><div class="muted bk-t">Starting</div><div class="bar"><i class="bk-p" style="width:0%"></i></div></div>`.html;
  document.body.append(overlay);
  return {
    say: (text, f) => { (overlay.querySelector('.bk-t') as HTMLElement).textContent = text; (overlay.querySelector('.bk-p') as HTMLElement).style.width = `${Math.round(f * 100)}%`; },
    done: () => overlay.remove(),
  };
}

async function runExport(p: Project): Promise<void> {
  const ui = progressPanel(`Backing up ${p.name}`);
  try { await exportProject(p.id, ui.say); toast('Backup downloaded'); }
  catch (e) { toast(e instanceof Error ? e.message : 'The backup stopped. Try again.', 'error', 8000); }
  finally { ui.done(); }
}

function pickBackup(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.zip,application/zip';
    input.onchange = () => resolve(input.files?.[0] ?? null);
    input.oncancel = () => resolve(null);
    input.click();
  });
}

async function readPicked(): Promise<OpenBackup | null> {
  const file = await pickBackup();
  if (!file) return null;
  try { return await openBackup(file); }
  catch (e) { toast(e instanceof Error ? e.message : 'That file can’t be read.', 'error', 7000); return null; }
}

const when = (iso: string) => new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });

async function runImport(p: Project, reload: () => void): Promise<void> {
  const src = await readPicked();
  if (!src) return;
  const ok = await confirmDialog({
    title: `Replace ${p.name} with this backup?`,
    body: html`<p>Its renders, model, levels, hotspots, brand kit and settings will be replaced with the ones in the backup of <b>${String(src.backup.project.name ?? 'a project')}</b> from ${when(src.backup.exportedAt)} (${formatBytes(src.size)}, ${src.backup.panoramas.length} renders).</p><p>The name, share link, billing and password stay as they are. This can’t be undone.</p>`,
    confirm: 'Replace project', danger: true,
  });
  if (!ok) return;
  const ui = progressPanel(`Restoring ${p.name}`);
  try { await restoreProject(p, src, ui.say); toast(`${p.name} restored`); reload(); }
  catch (e) { toast(e instanceof Error ? e.message : 'The import stopped. Try again.', 'error', 9000); }
  finally { ui.done(); }
}

async function importAsNew(studio: Studio): Promise<void> {
  const src = await readPicked();
  if (!src) return;
  const name = String(src.backup.project.name ?? 'Imported project');
  const ui = progressPanel(`Importing ${name}`);
  try {
    const p = await createProject(studio.id, name, String(src.backup.project.client_name ?? ''));
    await restoreProject(p, src, ui.say);
    toast(`${name} imported`);
    location.hash = `#/p/${p.id}`;
  } catch (e) { toast(e instanceof Error ? e.message : 'The import stopped. Try again.', 'error', 9000); }
  finally { ui.done(); }
}

async function runDelete(p: Project, reload: () => void): Promise<void> {
  if (p.state === 'published') {
    const go = await confirmDialog({
      title: 'Cancel the subscription first',
      body: html`<p><b>${p.name}</b> is published and paid for each month. Cancel its subscription in Billing first. Once it ends, the tour pauses and you can delete the project.</p>`,
      confirm: 'Open Billing',
    });
    if (go) location.hash = `#/p/${p.id}/billing`;
    return;
  }
  const ok = await confirmDialog({
    title: `Delete ${p.name}?`,
    body: html`<p>This deletes the project and everything in it: renders, model, hotspots, brand kit files, settings, visits and enquiries. It can’t be undone.</p><p>Export a backup first if you might need it again.</p>`,
    confirm: 'Delete project', danger: true, typeToConfirm: p.name,
  });
  if (!ok) return;
  try { await deleteProject(p.id); toast(`${p.name} deleted`); reload(); }
  catch (e) { toast(e instanceof Error ? e.message : 'Couldn’t delete the project.', 'error', 8000); }
}

export function newProjectView(root: HTMLElement, studio: Studio): void {
  const page = shell(root, { studioName: studio.name, active: 'projects' });
  page.innerHTML = html`
    <div class="page-head"><div><h1>New project</h1><div class="sub">Name it, then drop your renders, model and camera points.</div></div></div>
    <form class="box" id="np" style="max-width:520px;display:grid;gap:16px;padding:24px" novalidate>
      <div class="field"><label for="np-name">Project name</label><input id="np-name" placeholder="Enter project name" required></div>
      <div class="field"><label for="np-client">Client (optional)</label><input id="np-client" placeholder="Enter client name"></div>
      <div class="err" id="np-err" hidden></div>
      <div class="btn-row"><button class="btn primary" type="submit" id="np-go">Create project</button><a class="btn" href="#/">Cancel</a></div>
      <p class="muted" style="font-size:12.5px">New projects are free trials: up to ${PLAN.trialPanoLimit} renders and a private preview. Publish for $${PLAN.priceAud} AUD a month to share it.</p>
    </form>`.html;
  const form = $<HTMLFormElement>('#np', page);
  form.onsubmit = async (e) => {
    e.preventDefault();
    const name = $<HTMLInputElement>('#np-name', page).value.trim();
    const err = $('#np-err', page);
    if (name.length < 2) { err.textContent = 'Give the project a name.'; err.hidden = false; return; }
    $<HTMLButtonElement>('#np-go', page).disabled = true;
    try {
      const p = await createProject(studio.id, name, $<HTMLInputElement>('#np-client', page).value.trim());
      location.hash = `#/p/${p.id}/upload`;
    } catch (ex) {
      err.textContent = ex instanceof Error ? ex.message : 'Could not create the project.';
      err.hidden = false;
      $<HTMLButtonElement>('#np-go', page).disabled = false;
    }
  };
  $<HTMLInputElement>('#np-name', page).focus();
}
